import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { getDefaultClassNames } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

interface DateOfBirthPickerProps {
  value?: string;
  onChange: (value: string) => void;
  id?: string;
  hasError?: boolean;
  placeholder?: string;
}

function parseDateString(dateStr: string): Date | undefined {
  if (!dateStr) return undefined;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return undefined;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined;
  const d = new Date(year, month, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month ||
    d.getDate() !== day
  ) {
    return undefined;
  }
  return d;
}

function serializeDateToString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(date: Date, language: string): string {
  return new Intl.DateTimeFormat(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function DateOfBirthPicker({
  value,
  onChange,
  id,
  hasError,
  placeholder,
}: DateOfBirthPickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const dayPickerClassNames = getDefaultClassNames();

  const selectedDate = value ? parseDateString(value) : undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Adults only: the latest selectable birth date is exactly 18 years ago,
  // so the calendar cannot even offer an underage date of birth.
  const maxDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );

  const displayLabel = placeholder ?? t("Seleziona la data");

  const startYear = 1920;
  const endYear = maxDate.getFullYear();

  const startMonth = new Date(startYear, 0, 1);
  const endMonth = new Date(endYear, maxDate.getMonth(), 1);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            selectedDate ? "text-secondary" : "text-muted-800",
            hasError && "border-destructive",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <span className="flex-1 text-left">
            {selectedDate
              ? formatDisplayDate(selectedDate, i18n.language)
              : displayLabel}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          // Il Calendar condiviso è tarato per il booking (root lg:min-w-[480px]
          // e celle giorno w-full aspect-square col cerchio = intera cella):
          // nel popover DOB il giorno selezionato risultava un cerchio enorme.
          // Qui, senza toccare calendar.tsx: (1) vincoliamo la larghezza del
          // root; (2) togliamo lo sfondo/cerchio dalla CELLA (tenendone solo la
          // larghezza-colonna per l'allineamento); (3) un DayButton custom
          // disegna il cerchio come elemento interno a dimensione fissa centrato.
          classNames={{
            root: cn("w-[18rem]", dayPickerClassNames.root),
            day: cn(
              "flex aspect-square w-full min-w-[--cell-size] items-center justify-center p-0",
              dayPickerClassNames.day,
            ),
          }}
          components={{
            DayButton: ({ day, modifiers, className, ...props }) => (
              <button
                {...props}
                className={cn(
                  "flex size-8 items-center justify-center rounded-full text-sm font-normal leading-none transition-colors",
                  modifiers.selected && "bg-secondary text-secondary-foreground",
                  modifiers.today &&
                    !modifiers.selected &&
                    "bg-accent text-accent-foreground",
                  (modifiers.disabled || modifiers.outside) &&
                    "text-muted-foreground opacity-50",
                  className,
                )}
              >
                {day.date.getDate()}
              </button>
            ),
          }}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          selected={selectedDate}
          onSelect={(date) => {
            if (date) {
              onChange(serializeDateToString(date));
            }
            setOpen(false);
          }}
          disabled={(date) => date > maxDate}
          defaultMonth={selectedDate ?? new Date(1990, 0, 1)}
        />
      </PopoverContent>
    </Popover>
  );
}
